/**
 * Settings v2 — Billing tab content (CRM pattern, ui-management >= 1.21.5).
 *
 * Titled "Billing"; no credits card (the Credits tab owns credits). The plan
 * picker is outcomes-first, and the outcome to pre-tick arrives as a PROP from
 * the page's `?outcome=` — the picker never reads the URL itself. Billing
 * managers (Central `manageBilling`) get the actions; everyone else reads the
 * plan and is pointed at the workspace owner or an admin. Central decides.
 *
 * A plan Central did not name is UNKNOWN, never "Free": a 2xx without a
 * `planCode` (like a failed read) shows "Billing details are unavailable" with
 * Retry — calling it "Free" could tell a paying customer they are on the free
 * plan. (A 404 — no subscription row yet — is a failed read: unavailable too.)
 */
import { useCallback, useMemo, useState } from 'react';
import { BillingStatusCard } from '@levelup/ui-management/billing';
import { CentralPlanPicker, InvoicesSection, useCentralResource } from '@levelup/ui-management/central';
import { extractPortalUrl, type CentralBillingClient } from '@levelup/central-billing';

/** A plain outcome code, or nothing. */
const OUTCOME_CODE = /^[a-z0-9][a-z0-9_-]{0,63}$/i;

export const BILLING_ASK_OWNER =
  'Billing is managed by your workspace owner or an admin. Ask your workspace owner or an admin to change the plan.';

export interface BillingPanelProps {
  client: CentralBillingClient;
  workspaceId: string;
  workspaceType: string | null;
  canManage: boolean;
  centralBaseUrl: string;
  /** Absolute URL of this app's Billing tab (Stripe / portal return). */
  returnTo: string;
  /** `?view=plans` — the picker is open. */
  showPlans: boolean;
  /** `?outcome=<code>` — pre-ticked in the outcomes step. */
  outcome?: string | null;
  appNames?: Record<string, string>;
  onOpenPlans: () => void;
  /** Close the picker (the page drops `view` and `outcome`). */
  onClosePlans: () => void;
  onChanged: () => void;
  onUnauthenticated: () => void;
}

export function BillingPanel(props: BillingPanelProps) {
  const { client, workspaceId, canManage, showPlans, outcome, appNames, onUnauthenticated } = props;
  const handlers = useMemo(() => ({ onUnauthenticated }), [onUnauthenticated]);
  const sub = useCentralResource(useCallback(() => client.getSubscription(), [client]), handlers);
  const raw = sub.status === 'ready' ? (sub.data as unknown as Record<string, unknown> | null) : null;
  // Only a plan Central NAMED is shown; anything else is "unavailable" (never "Free").
  const planCode = typeof raw?.planCode === 'string' && raw.planCode.trim() ? raw.planCode : null;
  const data = planCode ? raw : null;
  const unavailable = sub.status === 'error' || (sub.status === 'ready' && !planCode);

  const preTick = outcome && OUTCOME_CODE.test(outcome) ? outcome : null;
  const outcomesFirst = useMemo(
    () => ({ ...(appNames ? { appNames } : {}), ...(preTick ? { initialOutcomes: [preTick] } : {}) }),
    [appNames, preTick],
  );

  const [portalError, setPortalError] = useState<string | null>(null);
  const openPortal = useCallback(async () => {
    setPortalError(null);
    const r = await client.openPortal({ workspaceId, returnTo: props.returnTo });
    if (!r.ok) {
      if (r.status === 401) onUnauthenticated();
      else setPortalError('The billing portal could not be opened right now.');
      return;
    }
    const url = extractPortalUrl(r.data);
    if (url) window.location.assign(url);
  }, [client, workspaceId, props.returnTo, onUnauthenticated]);

  const status = String(data?.status ?? 'none');
  const state = data
    ? {
        plan: {
          planCode: planCode as string,
          planName: typeof data.planName === 'string' && data.planName ? data.planName : (planCode as string),
          status: status as never,
          currentPeriodEnd: (data.currentPeriodEnd as string | null | undefined) ?? null,
          cancelAtPeriodEnd: data.cancelAtPeriodEnd === true,
          trialEndsAt: (data.trialEndsAt as string | null | undefined) ?? null,
        },
        credits: { balance: 0 },
        billing: { available: canManage && data.hasLiveSubscription === true, provider: 'stripe' as never },
        upgradeAvailable: false,
      }
    : null;
  const seats = data?.seats as { used?: number; limit?: number | null } | null | undefined;

  return (
    <section data-testid="billing-tab">
      <h3 data-testid="billing-title" style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>
        Billing
      </h3>
      {!canManage && (
        <p data-testid="billing-ask-owner" style={{ margin: '0 0 12px', fontSize: 13 }}>
          {BILLING_ASK_OWNER}
        </p>
      )}
      {unavailable && (
        <p role="alert" data-testid="billing-unavailable" style={{ fontSize: 13 }}>
          Billing details are unavailable right now.{' '}
          <button type="button" onClick={() => void sub.reload()}>
            Retry
          </button>
        </p>
      )}
      {state && (
        <BillingStatusCard
          state={state as never}
          showCredits={false}
          canManageBilling={canManage}
          onOpenBillingPortal={() => void openPortal()}
          pendingChange={(data?.pendingChange as never) ?? null}
          seats={typeof seats?.used === 'number' ? { used: seats.used, limit: seats.limit ?? null } : null}
          billingCycle={data?.billingCycle === 'month' ? 'month' : undefined}
        />
      )}
      {portalError && (
        <p role="alert" data-testid="billing-portal-error" style={{ fontSize: 13 }}>
          {portalError}
        </p>
      )}
      {!showPlans && !unavailable && (
        <button type="button" data-testid="billing-open-plans" onClick={props.onOpenPlans} style={{ marginTop: 16 }}>
          {canManage ? 'Change plan' : 'View plans'}
        </button>
      )}
      {showPlans && (
        <div data-testid="billing-plan-picker" style={{ marginTop: 20 }}>
          <CentralPlanPicker
            client={client}
            workspaceId={workspaceId}
            workspaceType={props.workspaceType}
            canManageBilling={canManage}
            centralBaseUrl={props.centralBaseUrl}
            returnTo={props.returnTo}
            onChanged={() => {
              void sub.reload();
              props.onChanged();
            }}
            onClose={props.onClosePlans}
            outcomesFirst={outcomesFirst}
          />
        </div>
      )}
      {canManage && <InvoicesSection client={client} {...handlers} className="mt-6" />}
    </section>
  );
}
